import { useEffect, useMemo, useState } from "react";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";
import { AxiosError } from "axios";
import { PersonFingerprintFormRule, PersonFingerprintFormSchema } from "@core/form/form.rule";
import { useFormDefaultPersonFingerprint } from "@core/form/form-default-person-fingerprint.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { GetPersonFingerprintModel } from "@core/model/query-model-person-fingerprint";
import {
  useQyCreatePersonFingerprint,
  useQyDeletePersonFingerprint,
  useQyGetPersonFingerprintByUserCode,
  useQyUpdatePersonFingerprint,
} from "@core/query/person-fingerprint.query";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import HeaderButton from "@shared/ui/header-button/header-button";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useFormDropdownPersonContext } from "../form-dropdown-person/form-dropdown-person.context";
import FormPersonFingerprint from "./form-person-fingerprint/form-person-fingerprint";
import "./person-fingerprint.scss";

export function PersonFingerprint() {
  const { getDefault } = useFormDefaultPersonFingerprint();
  const { showError, showSuccess } = useNotificationContext();
  const {
    mapCreatePersonFingerprint,
    mapUpdatePersonFingerprint,
    mapDeletePersonFingerprint,
  } = useFormApiMapper();
  const { selectedPersonData } = useFormDropdownPersonContext();
  const userCode = selectedPersonData?.code || "";
  const [fingerprintData, setFingerprintData] = useState<
    GetPersonFingerprintModel | undefined
  >();

  const formMethod = useForm<PersonFingerprintFormSchema>({
    defaultValues: getDefault(undefined, userCode),
    resolver: zodResolver(PersonFingerprintFormRule),
  });
  const { handleSubmit, reset, setValue } = formMethod;

  const handleGetApiSuccess = (data: GetPersonFingerprintModel[] | undefined) => {
    console.log("FINGERPRINT DATA", data);
    setFingerprintData(data?.[0] || undefined);
    reset(getDefault(data?.[0], userCode));
  };

  const handleGetApiError = (_err: AxiosError) => {
    setFingerprintData(undefined);
    reset(getDefault(undefined, userCode));
  };

  const { isLoading } = useQyGetPersonFingerprintByUserCode(
    userCode,
    !!userCode,
    handleGetApiSuccess,
    handleGetApiError
  );

  const isEditMode = !!fingerprintData?.code;

  const actions: ButtonAction[] = useMemo(() => {
    if (isEditMode) {
      return [
        { label: "Update", type: "split", value: ActionEnum.Update },
        { label: "Delete", type: "more", value: ActionEnum.Delete },
      ];
    }
    return [{ label: "Save", type: "primary", value: ActionEnum.Create }];
  }, [isEditMode]);

  useEffect(() => {
    if (userCode) {
      setValue("userCode", userCode);
    }
  }, [userCode, setValue]);

  const handleCreateApiSuccess = () => {
    showSuccess("Fingerprint registered successfully");
  };
  const { mutate: createPersonFingerprint } = useQyCreatePersonFingerprint(
    handleCreateApiSuccess
  );

  const handleUpdateApiSuccess = () => {
    showSuccess("Fingerprint updated successfully");
  };
  const { mutate: updatePersonFingerprint } = useQyUpdatePersonFingerprint(
    handleUpdateApiSuccess
  );

  const handleDeleteApiSuccess = () => {
    showSuccess("Fingerprint deleted successfully");
    setFingerprintData(undefined);
    reset(getDefault(undefined, userCode));
  };
  const { mutate: deletePersonFingerprint } = useQyDeletePersonFingerprint(
    handleDeleteApiSuccess
  );

  const handleValidate = (form: PersonFingerprintFormSchema) => {
    if (isEditMode && fingerprintData) {
      const formData = mapUpdatePersonFingerprint(form, fingerprintData.code);
      updatePersonFingerprint(formData);
      return;
    }

    const formData = mapCreatePersonFingerprint({
      ...form,
      userCode,
    });
    createPersonFingerprint(formData);
  };

  const handleValidateError = (err: FieldErrors<PersonFingerprintFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const acceptDelete = () => {
    if (!fingerprintData) {
      throw new Error("No fingerprint data found");
    }

    const form = mapDeletePersonFingerprint(fingerprintData.code);
    deletePersonFingerprint(form);
  };

  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Create:
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this fingerprint record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept: acceptDelete,
        });
        break;
    }
  };

  if (!selectedPersonData) {
    return (
      <div id="PersonFingerprint" className="person-fingerprint">
        <small>No person selected</small>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div id="PersonFingerprint" className="person-fingerprint">
        <SkeletonList count={2} />
      </div>
    );
  }

  return (
    <div id="PersonFingerprint" className="person-fingerprint">
      <span className="flex justify-end">
        <HeaderButton actions={actions} onAction={handleAction} />
      </span>

      <h4>Fingerprint</h4>
      {!isEditMode && (
        <p className="text-sm text-gray-500 mb-4">
          No fingerprint registered for this person yet.
        </p>
      )}

      <FormProvider {...formMethod}>
        <FormPersonFingerprint />
      </FormProvider>
    </div>
  );
}

export default PersonFingerprint;
